import { api } from '../axios';
import Authentication from './Authentication';
import Mongo from './Mongo';

import {
  CycleResponse,
  SuggestedSubjectsParams,
  CycleSuggestedResponse,
  CycleData,
  Availability,
  DaysOfWeekKeys,
  CycleUpdate,
  SubjectCycle,
  CycleDailySuggestedResponse,
  EveUpdate,
} from '../../types';
import Journey from './Journey';

const CYCLE_ENDPOINT = '/schedules/cycle';
const SUGGESTED_SUBJECTS_ENDPOINT = 'schedules/subjects';

class Cycle extends Mongo {
  public async createCycle({
    schedule_id,
    availability,
    subjects,
    week,
    cycle,
    duration,
    week_start,
  }: {
    schedule_id: string;
    availability: Availability[];
    subjects: SubjectCycle[];
    week: number;
    cycle: CycleUpdate[];
    duration: number;
    week_start: DaysOfWeekKeys;
  }): Promise<unknown | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const cyclePayload: CycleData = {
        user_email: loginData.user.email,
        schedule_id,
        availability,
        subjects,
        week,
        cycle,
        duration,
        week_start,
      };
      const response = api.post(CYCLE_ENDPOINT, cyclePayload, {
        headers: {
          UserEmail: loginData.user.email,
          'X-Org-Id': loginData.permissions[0].group.slug,
        },
      });

      await this.updateJourneyCycle();

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  public async updateJourneyCycle() {
    const sessionData = Authentication.loggedUser();
    if (!sessionData) return;
    const id = sessionData.journey._id;
    if (id && !sessionData.journey.study_control) {
      await Journey.update(id, {
        study_control: true,
      });
      sessionData.journey.study_control = true;
      Authentication.storeUser(sessionData);
    }
  }

  public async fetchCycles(
    schedule_id: string
  ): Promise<CycleResponse | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const response: CycleResponse = await api.get(CYCLE_ENDPOINT, {
        params: {
          where: {
            user_email: loginData.user.email,
            schedule_id,
          },
          sort: this.parseSortArr([['_created', 'desc']]),
        },
      });
      return response;
    } catch (error) {
      console.error(error);
    }
  }
  public async fetchSuggestedSubjects({
    schedule_id,
    paused,
    availability,
  }: SuggestedSubjectsParams): Promise<CycleSuggestedResponse | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const response: CycleSuggestedResponse = await api.post(
        SUGGESTED_SUBJECTS_ENDPOINT,
        {
          schedule_id,
          availability: availability,
          paused,
          user_email: loginData.user.email,
        }
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }
  public async fetchDailySubjectsSuggestions({
    id,
    day,
  }: {
    id: string;
    day: DaysOfWeekKeys;
  }): Promise<CycleDailySuggestedResponse> {
    const user = Authentication.loggedUser();
    if (!user) throw new Error('User not logged');
    return await api
      .get(`${CYCLE_ENDPOINT}/${id}/suggestion/${day}`)
      .then((response) => {
        return response;
      });
  }
  public async updateCyclePomodoros({
    cycle,
    id,
    etag,
  }: {
    cycle: CycleUpdate[];
    id: string;
    etag: string;
  }): Promise<void> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      await api.patch(
        `${CYCLE_ENDPOINT}/${id}`,
        {
          cycle,
        },
        {
          headers: {
            'If-Match': etag,
            UserEmail: loginData.user.email,
            'X-Org-Id': loginData.permissions[0].group.slug,
          },
        }
      );
    } catch (error) {
      console.error(error);
    }
  }
  public async updateCycle({
    cycle,
  }: {
    cycle: Partial<CycleData>;
  }): Promise<EveUpdate | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const { _etag, _id } = cycle;
      const response = await api.patch<EveUpdate>(
        `${CYCLE_ENDPOINT}/${_id as string}`,
        this.removeMongoAttrs(cycle),
        {
          headers: {
            'If-Match': _etag,
            UserEmail: loginData.user.email,
            'X-Org-Id': loginData.permissions[0].group.slug,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }
  public async deleteCycle({
    id,
    etag,
  }: {
    id: string;
    etag: string;
  }): Promise<void> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      await api.delete(`${CYCLE_ENDPOINT}/${id}`, {
        headers: {
          'If-Match': etag,
          UserEmail: loginData.user.email,
          'X-Org-Id': loginData.permissions[0].group.slug,
        },
      });
    } catch (error) {
      console.error(error);
    }
  }
}

export default new Cycle();
